import {
  POINTS_SCHEMA_VERSION,
  type FieldMultiplier,
  type BuyInMultiplier,
  type PointsDefinition,
  type PointsScope,
  type PointsTableRow,
} from './definition';

/**
 * Starting points — the four placement tables §9.3 asks for, and the two whole definitions the
 * empty state offers instead of a blank form.
 *
 * A preset is a *starting shape*, never a claim about what a room pays. Nothing here is applied
 * without the operator choosing it, and every figure is editable the moment it lands.
 */

export interface PlacementPreset {
  id: string;
  label: string;
  /** One line under the button. Says what the shape is, and where it came from. */
  description: string;
  rows: () => PointsTableRow[];
}

/**
 * SHRPO's published ladder — the reference system in §5 of the spec, and the one the engine's
 * golden vectors are checked against. 100 for a win down to a flat 10 from 28th.
 */
export const SHRPO_ROWS: readonly PointsTableRow[] = [
  { from: 1, to: 1, points: 100 },
  { from: 2, to: 2, points: 70 },
  { from: 3, to: 3, points: 50 },
  { from: 4, to: 4, points: 44 },
  { from: 5, to: 5, points: 38 },
  { from: 6, to: 6, points: 33 },
  { from: 7, to: 7, points: 28 },
  { from: 8, to: 8, points: 24 },
  { from: 9, to: 9, points: 20 },
  { from: 10, to: 18, points: 15 },
  { from: 19, to: 27, points: 12 },
  { from: 28, to: null, points: 10 },
];

/**
 * SHRPO's field-size ladder: 1.0 up to a 50-runner field, rising to 2.0 once a field passes 800.
 * Doubles as the default the field weighting switches on with, since it is the one ladder in this
 * codebase that has been checked against published standings.
 */
export const SHRPO_FIELD_BANDS: FieldMultiplier = {
  metric: 'entries',
  cap: null,
  floor: null,
  bands: [
    { from: 4, to: 50, value: 1.0 },
    { from: 51, to: 100, value: 1.1 },
    { from: 101, to: 150, value: 1.2 },
    { from: 151, to: 200, value: 1.3 },
    { from: 201, to: 300, value: 1.4 },
    { from: 301, to: 400, value: 1.5 },
    { from: 401, to: 500, value: 1.6 },
    { from: 501, to: 600, value: 1.7 },
    { from: 601, to: 700, value: 1.8 },
    { from: 701, to: 800, value: 1.9 },
    { from: 801, to: null, value: 2.0 },
  ],
};

/**
 * A plain three-step buy-in ladder to switch on and then edit. Deliberately coarse: unlike the
 * field ladder above, no published system this codebase has checked uses these numbers, so an
 * elaborate default would only look more authoritative than it is.
 */
export const DEFAULT_BUY_IN_BANDS: BuyInMultiplier = {
  metric: 'buyIn',
  cap: null,
  floor: null,
  bands: [
    { from: 0, to: 999, value: 1.0 },
    { from: 1000, to: 4999, value: 1.25 },
    { from: 5000, to: null, value: 1.5 },
  ],
};

/**
 * The WSOP Circuit shape: the published top three (50 / 37.5 / 30), then a steady 15% step down
 * per place, rounded to the nearest quarter point, until it reaches the 2.5 floor — which it does
 * at 18th, and everything from there down earns that.
 *
 * Generated from the rule rather than typed out, so what it is doing is visible. It reproduces
 * the *shape* of the published structure, not a copy of it; check it against the current rules
 * before publishing anything scored by it.
 */
function wsopCircuitRows(): PointsTableRow[] {
  const rows: PointsTableRow[] = [
    { from: 1, to: 1, points: 50 },
    { from: 2, to: 2, points: 37.5 },
    { from: 3, to: 3, points: 30 },
  ];

  const floor = 2.5;
  let place = 4;

  for (;;) {
    const points = Math.round(30 * 0.85 ** (place - 3) * 4) / 4;
    if (points <= floor) break;
    rows.push({ from: place, to: place, points });
    place += 1;
  }

  rows.push({ from: place, to: null, points: floor });
  return rows;
}

/** Everyone who reaches a paid place earns the same. The simplest table there is. */
function flatRows(): PointsTableRow[] {
  return [{ from: 1, to: null, points: 10 }];
}

/** 100 for a win, five points less per place, levelling off at 5 from 20th down. */
function linearDecayRows(): PointsTableRow[] {
  const rows: PointsTableRow[] = [];
  const step = 5;
  const floor = 5;

  for (let place = 1; ; place++) {
    const points = 100 - (place - 1) * step;
    if (points <= floor) {
      rows.push({ from: place, to: null, points: floor });
      return rows;
    }
    rows.push({ from: place, to: place, points });
  }
}

export const PLACEMENT_PRESETS: readonly PlacementPreset[] = [
  {
    id: 'shrpo',
    label: 'SHRPO',
    description: '100 for a win down to a flat 10 from 28th. The published Seminole ladder.',
    rows: () => SHRPO_ROWS.map((row) => ({ ...row })),
  },
  {
    id: 'wsop-circuit',
    label: 'WSOP Circuit',
    description: '50 / 37.5 / 30 at the top, easing down to a 2.5 minimum from 18th.',
    rows: wsopCircuitRows,
  },
  {
    id: 'flat',
    label: 'Flat',
    description: 'Every paid place earns the same. Volume decides the board.',
    rows: flatRows,
  },
  {
    id: 'linear',
    label: 'Straight line',
    description: '100 for a win, five points less per place, levelling off at 5.',
    rows: linearDecayRows,
  },
];

// ---------------------------------------------------------------- expressions

export interface ExpressionPreset {
  id: string;
  label: string;
  /** One line under the button. What shape it makes, and where it came from. */
  description: string;
  expression: string;
}

/**
 * §9.4's two starting formulas.
 *
 * Both are the *published shape* of a well-known system rather than a copy of one, for the same
 * reason `PLACEMENT_PRESETS` says so about the WSOP Circuit ladder: the real ones are revised, and
 * a preset that claimed to be current would go quietly out of date inside a room's season. Check
 * either against the current rules before publishing anything scored by it.
 *
 * The first is §4.3's own worked example, so it is also the one the engine's acceptance tests run
 * over the 2026 SHRPO results.
 */
export const EXPRESSION_PRESETS: readonly ExpressionPreset[] = [
  {
    id: 'gpi',
    label: 'GPI-style',
    description:
      'How far up the field you finished, weighted by a flattened buy-in. Diminishing returns at both ends: a $20,000 event is worth more than a $400 one, but nothing like fifty times more.',
    expression: 'ln(fieldSize / effectivePlace) * root(clamp(buyIn, 400, 20000) / 1000, 4.5)',
  },
  {
    id: 'wsop-root',
    label: 'Root formula',
    description:
      'The square-root shape: 10 × √(entries ÷ place), lifted by the buy-in’s order of magnitude. Steeper at the top than the GPI shape, so a win is worth relatively more.',
    expression: '10 * sqrt(fieldSize / effectivePlace) * (1 + log10(buyIn))',
  },
];

// ---------------------------------------------------------------- whole definitions

/** `yyyy-mm-dd` for a date, in the room's own calendar — no offset is modelled anywhere here. */
function isoDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * The preview pane's opening **test scope** (§9.8) — the last three months, everything in it.
 *
 * This is a test harness and never a setting. It exists so the board on the right has something
 * to score the moment the screen opens, rather than making an operator fill in a date range before
 * the feature that makes this a design tool does anything. A leaderboard adopting the system
 * defines its own events (§9.9) and never sees this.
 *
 * Backwards rather than forwards, unlike a real leaderboard's: results already exist for the
 * months behind today and, by definition, none exist for the months ahead. A window over the
 * future would open every new system on an empty board.
 */
export function blankScope(): PointsScope {
  const end = new Date();
  const start = new Date(end);
  start.setMonth(start.getMonth() - 3);

  return {
    startDate: isoDate(start),
    endDate: isoDate(end),
    includeTags: [],
    excludeTags: [],
    includeEventIds: [],
    excludeEventIds: [],
    minFieldSize: 0,
  };
}

/**
 * A system to start typing into: the SHRPO ladder with no weighting, no participation points, and
 * the deal rule every serious system uses. Sums every event.
 *
 * It carries no dates, because a points system has none (§3.2). What events it is tried against
 * while being designed is `blankScope`, which lives in the preview pane.
 */
export function blankDefinition(name = ''): PointsDefinition {
  return {
    schemaVersion: POINTS_SCHEMA_VERSION,
    name,
    description: null,
    scoring: { type: 'table', rows: SHRPO_ROWS.map((row) => ({ ...row })) },
    fieldMultiplier: null,
    buyInMultiplier: null,
    participation: {
      mode: 'none',
      pointsPerEntry: 1,
      countEntries: 'each',
      applyFieldMultiplier: true,
      applyBuyInMultiplier: true,
      countBustedBulletsWhenCashed: true,
    },
    dealHandling: 'worstOfDeal',
    reentryPolicy: 'bestResultOnly',
    aggregation: { mode: 'sumAll' },
    tiebreakers: ['alphabeticalByLastName'],
    display: { decimalPlaces: 2 },
  };
}

/**
 * The full reference system from §5 — the one §9.7 offers instead of a blank form when a room has
 * nothing yet, and the one the engine's acceptance tests reproduce against published standings.
 *
 * One deliberate difference from §5: `countBustedBulletsWhenCashed` is left at the product default
 * of `true`. The reference config sets it `false` because SHRPO's published rules are ambiguous
 * and the golden vectors only hold under that reading — but "every entry a player pays for earns
 * something" is the more intuitive answer, and this is a starting point for a room's own system
 * rather than a reproduction of somebody else's.
 */
export function shrpoDefinition(name = 'SHRPO-style tournament leaderboard'): PointsDefinition {
  const base = blankDefinition(name);

  return {
    ...base,
    description: 'Placement table × field size, everyone in a chop scores the lowest place.',
    scoring: { type: 'table', rows: SHRPO_ROWS.map((row) => ({ ...row })) },
    fieldMultiplier: {
      ...SHRPO_FIELD_BANDS,
      bands: SHRPO_FIELD_BANDS.bands.map((band) => ({ ...band })),
    },
    participation: {
      mode: 'nonCashingEntriesOnly',
      pointsPerEntry: 1,
      countEntries: 'each',
      applyFieldMultiplier: true,
      applyBuyInMultiplier: true,
      countBustedBulletsWhenCashed: true,
    },
  };
}
